import { SquarePen, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { CategoryRowProps } from '@/types/category';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { NavigationMenuItem } from '@/components/ui/navigation-menu';
import { ALL } from '@/constants';
import { cn } from '@/lib/utils';

export function CategoryRow({
  item,
  selectedCategoryId,
  onSelectCategoryId,
  onEdit,
  onDelete,
}: CategoryRowProps) {
  const { id, name, editable, total } = item;
  const isCurrentItemSelected = selectedCategoryId === item.id;
  const { t } = useTranslation();
  const isAllCategory = id === ALL;

  return (
    <NavigationMenuItem value={String(id)}>
      <div
        className={cn(
          'group/category-row hover:bg-muted flex w-full items-center gap-1 rounded-md px-2 py-1 transition-colors',
          { 'bg-train-table-selected hover:bg-train-table-selected': isCurrentItemSelected }
        )}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn(
            'text-muted-foreground min-h-12 min-w-0 flex-1 justify-start px-1 py-3 font-normal hover:bg-transparent',
            {
              'text-foreground': isCurrentItemSelected,
            }
          )}
          aria-label={t('common:configuration.controlMessages.selectCategory', { name })}
          onClick={() => onSelectCategoryId(id)}
        >
          <span
            className={cn('text-foreground', { 'text-muted-foreground': !isCurrentItemSelected })}
          >
            {name}
          </span>
        </Button>
        <div className="pointer-events-none flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover/category-row:pointer-events-auto group-hover/category-row:opacity-100">
          {onEdit && !isAllCategory && editable && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground h-8 w-8 hover:bg-transparent"
              onClick={(e) => {
                e.stopPropagation();
                onEdit({ id: Number(id), name, editable, totalMessages: total });
              }}
            >
              <SquarePen className="h-4 w-4" />
            </Button>
          )}
          {onDelete && !isAllCategory && editable && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground h-8 w-8 hover:bg-transparent"
              onClick={(e) => {
                e.stopPropagation();
                onDelete({ id: Number(id), name, editable, totalMessages: total });
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Badge
          variant="secondary"
          className={cn('rounded-full', {
            'bg-background text-foreground': isCurrentItemSelected,
          })}
        >
          {total}
        </Badge>
      </div>
    </NavigationMenuItem>
  );
}
